// ==========================================================
// app/cgu.tsx — Conditions Générales d'Utilisation v1.2
// ==========================================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import LegalDocumentScreen, { LegalSection } from '../components/LegalDocumentScreen';

const VIOLET       = '#6411A9';
const VIOLET_LIGHT = '#9B30E8';
const GOLD         = '#F5A623';
const SUCCESS      = '#00A86B';
const DANGER       = '#E8003D';
const BLUE         = '#3B82F6';

function useSections(t: (key: string, opts?: any) => any): LegalSection[] {
  return [
    {
      id: 'presentation', num: '01', icon: 'apps-outline', color: VIOLET_LIGHT,
      title: t('cgu.sections.presentation.title'),
      content: [
        { type: 'paragraph', text: t('cgu.sections.presentation.p1') },
        { type: 'paragraph', text: t('cgu.sections.presentation.p2') },
        { type: 'highlight', text: t('cgu.sections.presentation.highlight1'), icon: 'checkmark-circle' },
      ],
    },
    {
      id: 'inscription', num: '02', icon: 'person-add-outline', color: SUCCESS,
      title: t('cgu.sections.inscription.title'),
      content: [
        { type: 'subtitle', text: t('cgu.sections.inscription.sub1') },
        { type: 'paragraph', text: t('cgu.sections.inscription.p1') },
        { type: 'bullet', items: t('cgu.sections.inscription.bullets1', { returnObjects: true }) },
        { type: 'subtitle', text: t('cgu.sections.inscription.sub2') },
        { type: 'paragraph', text: t('cgu.sections.inscription.p2') },
        { type: 'subtitle', text: t('cgu.sections.inscription.sub3') },
        { type: 'highlight', text: t('cgu.sections.inscription.highlight1'), icon: 'lock-open' },
        { type: 'subtitle', text: t('cgu.sections.inscription.sub4') },
        { type: 'highlight', text: t('cgu.sections.inscription.highlight2'), icon: 'people' },
      ],
    },
    {
      id: 'publications', num: '03', icon: 'create-outline', color: VIOLET,
      title: t('cgu.sections.publications.title'),
      content: [
        { type: 'subtitle', text: t('cgu.sections.publications.sub1') },
        { type: 'bullet', items: t('cgu.sections.publications.bullets1', { returnObjects: true }) },
        { type: 'paragraph', text: t('cgu.sections.publications.p1') },
        { type: 'subtitle', text: t('cgu.sections.publications.sub2') },
        { type: 'bullet', items: t('cgu.sections.publications.bullets2', { returnObjects: true }) },
        { type: 'subtitle', text: t('cgu.sections.publications.sub3') },
        { type: 'bullet', items: t('cgu.sections.publications.bullets3', { returnObjects: true }) },
        { type: 'subtitle', text: t('cgu.sections.publications.sub4') },
        { type: 'highlight', text: t('cgu.sections.publications.highlight1'), icon: 'ribbon' },
        { type: 'subtitle', text: t('cgu.sections.publications.sub5') },
        { type: 'paragraph', text: t('cgu.sections.publications.p2') },
      ],
    },
    {
      id: 'abonnements', num: '04', icon: 'card-outline', color: GOLD,
      title: t('cgu.sections.abonnements.title'),
      content: [
        { type: 'subtitle', text: t('cgu.sections.abonnements.sub1') },
        { type: 'bullet', items: t('cgu.sections.abonnements.bullets1', { returnObjects: true }) },
        { type: 'subtitle', text: t('cgu.sections.abonnements.sub2') },
        { type: 'paragraph', text: t('cgu.sections.abonnements.p1') },
        { type: 'subtitle', text: t('cgu.sections.abonnements.sub3') },
        { type: 'highlight', text: t('cgu.sections.abonnements.highlight1'), icon: 'alert-circle' },
      ],
    },
    {
      id: 'messagerie', num: '05', icon: 'chatbubbles-outline', color: VIOLET_LIGHT,
      title: t('cgu.sections.messagerie.title'),
      content: [
        { type: 'subtitle', text: t('cgu.sections.messagerie.sub1') },
        { type: 'paragraph', text: t('cgu.sections.messagerie.p1') },
        { type: 'subtitle', text: t('cgu.sections.messagerie.sub2') },
        { type: 'paragraph', text: t('cgu.sections.messagerie.p2') },
        { type: 'highlight', text: t('cgu.sections.messagerie.highlight1'), icon: 'heart' },
      ],
    },
    {
      id: 'publicite', num: '06', icon: 'megaphone-outline', color: '#E67E22',
      title: t('cgu.sections.publicite.title'),
      content: [
        { type: 'paragraph', text: t('cgu.sections.publicite.p1') },
        { type: 'highlight', text: t('cgu.sections.publicite.highlight1'), icon: 'ban' },
      ],
    },
    {
      id: 'concours', num: '07', icon: 'trophy-outline', color: GOLD,
      title: t('cgu.sections.concours.title'),
      content: [
        { type: 'subtitle', text: t('cgu.sections.concours.sub1') },
        { type: 'bullet', items: t('cgu.sections.concours.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('cgu.sections.concours.highlight1'), icon: 'ribbon' },
        { type: 'subtitle', text: t('cgu.sections.concours.sub2') },
        { type: 'paragraph', text: t('cgu.sections.concours.p1') },
        { type: 'subtitle', text: t('cgu.sections.concours.sub3') },
        { type: 'paragraph', text: t('cgu.sections.concours.p2') },
        { type: 'subtitle', text: t('cgu.sections.concours.sub4') },
        { type: 'highlight', text: t('cgu.sections.concours.highlight2'), icon: 'flash' },
      ],
    },
    {
      id: 'boutiques', num: '08', icon: 'storefront-outline', color: VIOLET,
      title: t('cgu.sections.boutiques.title'),
      content: [
        { type: 'subtitle', text: t('cgu.sections.boutiques.sub1') },
        { type: 'paragraph', text: t('cgu.sections.boutiques.p1') },
        { type: 'highlight', text: t('cgu.sections.boutiques.highlight1'), icon: 'link' },
        { type: 'subtitle', text: t('cgu.sections.boutiques.sub2') },
        { type: 'paragraph', text: t('cgu.sections.boutiques.p2') },
        { type: 'subtitle', text: t('cgu.sections.boutiques.sub3') },
        { type: 'paragraph', text: t('cgu.sections.boutiques.p3') },
      ],
    },
    {
      id: 'moderation', num: '09', icon: 'shield-outline', color: SUCCESS,
      title: t('cgu.sections.moderation.title'),
      content: [
        { type: 'subtitle', text: t('cgu.sections.moderation.sub1') },
        { type: 'paragraph', text: t('cgu.sections.moderation.p1') },
        { type: 'subtitle', text: t('cgu.sections.moderation.sub2') },
        { type: 'bullet', items: t('cgu.sections.moderation.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('cgu.sections.moderation.highlight1'), icon: 'information-circle' },
      ],
    },
    {
      id: 'api', num: '10', icon: 'code-slash-outline', color: DANGER,
      title: t('cgu.sections.api.title'),
      content: [
        { type: 'paragraph', text: t('cgu.sections.api.p1') },
        { type: 'subtitle', text: t('cgu.sections.api.sub1') },
        { type: 'bullet', items: t('cgu.sections.api.bullets1', { returnObjects: true }) },
        { type: 'subtitle', text: t('cgu.sections.api.sub2') },
        { type: 'highlight', text: t('cgu.sections.api.highlight1'), icon: 'alert-circle' },
        { type: 'subtitle', text: t('cgu.sections.api.sub3') },
        { type: 'paragraph', text: t('cgu.sections.api.p2') },
      ],
    },
    {
      id: 'disponibilite', num: '11', icon: 'cloud-outline', color: BLUE,
      title: t('cgu.sections.disponibilite.title'),
      content: [
        { type: 'paragraph', text: t('cgu.sections.disponibilite.p1') },
        { type: 'paragraph', text: t('cgu.sections.disponibilite.p2') },
      ],
    },
    {
      id: 'modifications', num: '12', icon: 'refresh-outline', color: VIOLET_LIGHT,
      title: t('cgu.sections.modifications.title'),
      content: [
        { type: 'paragraph', text: t('cgu.sections.modifications.p1') },
        { type: 'highlight', text: t('cgu.sections.modifications.highlight1'), icon: 'checkmark-circle' },
      ],
    },
    {
      id: 'droit', num: '13', icon: 'globe-outline', color: GOLD,
      title: t('cgu.sections.droit.title'),
      content: [
        { type: 'paragraph', text: t('cgu.sections.droit.p1') },
      ],
    },
    {
      id: 'contact', num: '14', icon: 'mail-outline', color: SUCCESS,
      title: t('cgu.sections.contact.title'),
      content: [
        { type: 'highlight', text: 'harmonia@harmoniaworld.world', icon: 'mail' },
        { type: 'paragraph', text: t('cgu.sections.contact.p1') },
      ],
    },
  ];
}

export default function CGUScreen() {
  const { t } = useTranslation();
  const SECTIONS = useSections(t);

  return (
    <LegalDocumentScreen
      title={t('cgu.title')}
      version="v1.2"
      sections={SECTIONS}
      currentRoute="/cgu"
      stats={[
        { value: '14', label: t('cgu.stats.sections') },
        { isIcon: true, iconName: 'document-text', iconColor: '#FFC947', label: t('cgu.stats.law') },
        { isIcon: true, iconName: 'shield-checkmark', iconColor: '#FFC947', label: t('cgu.stats.compliant') },
      ]}
    />
  );
}
